import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CurrencyContext = createContext();

const FX_FALLBACK = 1.08; // EUR → USD Fallback
const FX_REFRESH_MS = 60 * 60 * 1000; // 60 Minuten

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => {
    const stored = localStorage.getItem('red_currency');
    return stored === 'USD' ? 'USD' : 'EUR';
  });

  const [fxRate, setFxRate] = useState(() => {
    const stored = parseFloat(localStorage.getItem('red_fx_eur_usd') || '');
    return Number.isFinite(stored) && stored > 0 ? stored : FX_FALLBACK;
  });

  useEffect(() => {
    localStorage.setItem('red_currency', currency);
  }, [currency]);

  const refreshFx = useCallback(async () => {
    const tryFetch = async (url, extract) => {
      try {
        const res = await fetch(url);
        if (!res.ok) return null;
        const json = await res.json();
        const rate = extract(json);
        return Number.isFinite(rate) && rate > 0 ? rate : null;
      } catch {
        return null;
      }
    };
    // 1. open.er-api.com (CORS-frei) — Base=EUR, Rate für USD
    let rate = await tryFetch(
      'https://open.er-api.com/v6/latest/EUR',
      j => j?.rates?.USD
    );
    // 2. Fallback: frankfurter.app
    if (!rate) {
      rate = await tryFetch(
        'https://api.frankfurter.app/latest?from=EUR&to=USD',
        j => j?.rates?.USD
      );
    }
    if (rate) {
      setFxRate(rate);
      localStorage.setItem('red_fx_eur_usd', String(rate));
    }
  }, []);

  useEffect(() => {
    refreshFx();
    const interval = setInterval(refreshFx, FX_REFRESH_MS);
    return () => clearInterval(interval);
  }, [refreshFx]);

  const setCurrency = useCallback((c) => {
    setCurrencyState(c === 'USD' ? 'USD' : 'EUR');
  }, []);

  const symbol = currency === 'USD' ? '$' : '€';
  const locale = currency === 'USD' ? 'en-US' : 'de-DE';
  const cgParam = currency.toLowerCase(); // für CoinGecko vs_currency

  const convert = useCallback(
    (eurValue) => {
      if (eurValue == null || !Number.isFinite(eurValue)) return eurValue;
      return currency === 'USD' ? eurValue * fxRate : eurValue;
    },
    [currency, fxRate]
  );

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, symbol, locale, fxRate, convert, cgParam }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export const useCurrency = () => useContext(CurrencyContext);
